'use client';
import Script from 'next/script';
import React, { useEffect } from 'react';

import { ALL_AD_SLOTS as allAdSlots } from '@/constants/ads.constants';
import { useAds } from '@/providers/ads';

// Official GPT sources.
const GPT_STANDARD_URL = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

export function InitializeGPT() {
  return <Script src={GPT_STANDARD_URL} async crossOrigin='anonymous' />;
}

export function AdSlot({
  id,
  wrapperClassName
}: {
  id: string;
  wrapperClassName?: string;
}) {
  const slot = allAdSlots[id];
  const { size } = slot;
  const { defineSlot, destroySlot } = useAds();

  useEffect(() => {
    defineSlot(id);

    return () => {
      // Cleanup ad slot on unmount.
      destroySlot(id);
    };
  }, [id, defineSlot, destroySlot]);

  return (
    <div
      className={wrapperClassName}
      style={getMinimumSlotSize(size)}
      id={id}
    />
  );
}

/**
 * Determine minimum width and height values for an ad slot container
 * based on the configured slot sizes.
 *
 * This function is only provided for example purposes. See
 * [Minimize layout shift](https://developers.google.com/publisher-tag/guides/minimize-layout-shift)
 * to learn more about strategies for sizing ad slot containers.
 */
function getMinimumSlotSize(size: [number, number] | [number, number][]) {
  const maxValue = Number.MAX_VALUE;

  let minW = Number.MAX_VALUE;
  let minH = Number.MAX_VALUE;

  if (Array.isArray(size)) {
    // Convert googletag.SingleSize to googletag.MultiSize for convenience.
    const sizes = size.length <= 2 && !Array.isArray(size[0]) ? [size] : size;

    for (const size of sizes) {
      if (
        Array.isArray(size) &&
        typeof size[0] === 'number' &&
        typeof size[1] === 'number'
      ) {
        minW = Math.min(size[0], minW);
        minH = Math.min(size[1], minH);
      }
    }
  }

  return minW < maxValue && minH < maxValue
    ? // Static ad slot.
      { minWidth: `${minW}px`, minHeight: `${minH}px` }
    : // Fluid ad slot.
      { minWidth: '50%' };
}
